import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Grid, NotFound, Paper, Stack, Typography, Box } from '@esgian/esgianui';
import { UpcomingVesselPortCallsTable, VesselPortCallsTable } from '@components/Tables';
import { OperatorActivityProfileChart, OperatorAvgSpeedChart } from './index';
import { TextWithTooltipIcon } from '@components';
import moment from 'moment';
import { useSegment } from '@hooks/useSegment';
import { useParams } from 'react-router-dom';
import {
  getAverageMonthlySpeed,
  getLatestPortCalls,
  getMonthlyPercentActivityStatus,
  getUpcomingPortCalls
} from '@api';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import { DATE_FORMAT, DEFAULT_NUM_PORT_CALLS, GEOGRAPHICAL_TYPES } from '@constants';
import { DateRangePicker } from '@components/Inputs';
import { useQuery } from '@tanstack/react-query';
import {
  OPERATOR_ACTIVITY_SPEED_RORO_TITLE,
  OPERATOR_ACTIVITY_SPEED_MPP_TITLE
} from '@constants/tooltips';

let defaultPeriod = {
  start: moment().subtract(1, 'year').startOf('month'),
  end: moment()
};
function OperatorActivitySection({ operatorShortName = '' }) {
  const { isRoRo, segment } = useSegment();
  const params = useParams();
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const operatorType = useSelector(getOperatorType);

  const [activityDates, setActivityDates] = useState({ ...defaultPeriod });
  const [speedDates, setSpeedDates] = useState({ ...defaultPeriod });

  const avgSpeedDataQuery = useQuery({
    queryKey: [
      'operatorAvgSpeedData',
      {
        OperatorId: parseInt(params.id),
        SegmentTypeId: segment.id,
        StartDate: moment(speedDates.start).format(DATE_FORMAT),
        EndDate: moment(speedDates.end).format(DATE_FORMAT),
        OperatorTypeId: operatorType,
        CommercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!speedDates.start && !!speedDates.end && !!params.id && !!segment.id,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getAverageMonthlySpeed(queryKey[1], signal)
        .then((resp) => resp)
        .catch(() => {
          return null;
        })
  });

  const upcomingPortCallsQuery = useQuery({
    queryKey: [
      'oprUpcomingPortCalls',
      {
        operatorIds: [parseInt(params.id)],
        numberOfIncomingEntries: DEFAULT_NUM_PORT_CALLS,
        segmentTypeId: segment.id,
        operatorTypeId: operatorType,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!params.id && !!segment.id,
    placeholderData: [],
    queryFn: ({ signal, queryKey }) =>
      getUpcomingPortCalls(queryKey[1], signal)
        .then((result) => result)
        .catch(() => [])
  });

  const lastPortCallsQuery = useQuery({
    queryKey: [
      'oprLastPortCalls',
      {
        geographicalType: GEOGRAPHICAL_TYPES.ALL,
        segmentTypeId: segment.id,
        CommercialCategoryId: commercialCategoryType,
        lastPortCallType: 0,
        numberOfPortCalls: DEFAULT_NUM_PORT_CALLS,
        operators: [parseInt(params.id)]
      }
    ],
    enabled: !!params.id && !!segment.id,
    placeholderData: [],
    queryFn: ({ signal, queryKey }) =>
      getLatestPortCalls(queryKey[1], signal)
        .then((result) => result)
        .catch(() => [])
  });

  const activityDataQuery = useQuery({
    queryKey: [
      'operatorActivityData',
      {
        OperatorId: parseInt(params.id),
        SegmentTypeId: segment.id,
        StartDate: moment(activityDates.start).format(DATE_FORMAT),
        EndDate: moment(activityDates.end).format(DATE_FORMAT),
        OperatorTypeId: operatorType,
        CommercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!activityDates.start && !!activityDates.end && !!params.id && !!segment.id,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getMonthlyPercentActivityStatus(queryKey[1], signal)
        .then((resp) => resp)
        .catch(() => {
          return null;
        })
  });

  const noData = useMemo(() => {
    if (
      avgSpeedDataQuery.isFetching ||
      upcomingPortCallsQuery.isFetching ||
      lastPortCallsQuery.isFetching ||
      activityDataQuery.isFetching
    )
      return false;
    return (
      !avgSpeedDataQuery.data &&
      !activityDataQuery.data &&
      !upcomingPortCallsQuery.data?.length &&
      !lastPortCallsQuery.data?.length
    );
  }, [avgSpeedDataQuery, upcomingPortCallsQuery, lastPortCallsQuery, activityDataQuery]);
  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {noData ? (
            <Grid item xs={12}>
              <NotFound
                show={true}
                header={'No Data to load'}
                text={'Please try refreshing the page'}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={6}>
                <Paper sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack>
                      <TextWithTooltipIcon
                        label={'Average speed'}
                        tooltipText={
                          isRoRo
                            ? OPERATOR_ACTIVITY_SPEED_RORO_TITLE
                            : OPERATOR_ACTIVITY_SPEED_MPP_TITLE
                        }
                        labelVariant={'h6'}
                      />
                      <Box sx={{ width: '10em' }}>
                        <DateRangePicker
                          inputVariant={'standard'}
                          startDate={speedDates.start}
                          endDate={speedDates.end}
                          onClose={(start, end) => {
                            setSpeedDates({
                              start: start,
                              end: end.isSame(moment(), 'month') ? moment() : end
                            });
                          }}
                        />
                      </Box>
                    </Stack>
                    <Divider />
                    <OperatorAvgSpeedChart
                      loading={avgSpeedDataQuery.isFetching}
                      avgSpeedData={avgSpeedDataQuery.data}
                      fileName={`${operatorShortName}-average-speed`}
                    />
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack>
                      <TextWithTooltipIcon
                        label={<Typography variant={'h6'}>Activity profile</Typography>}
                        tooltipText={
                          <Stack>
                            <Typography variant={'caption'}>
                              Transit: Vessel Speed &gt; 3 kn
                            </Typography>
                            <Typography variant={'caption'}>
                              Standby: 0 kn &lt; Vessel Speed &le; 3 kn
                            </Typography>
                            <Typography variant={'caption'}>At Port: Vessel at port</Typography>
                          </Stack>
                        }
                      />
                      <Box sx={{ width: '10em' }}>
                        <DateRangePicker
                          inputVariant={'standard'}
                          startDate={activityDates.start}
                          endDate={activityDates.end}
                          onClose={(start, end) => {
                            setActivityDates({ start: start, end: end });
                          }}
                        />
                      </Box>
                    </Stack>
                    <Divider />
                    <OperatorActivityProfileChart
                      activityStatusData={activityDataQuery.data}
                      loading={activityDataQuery.isFetching}
                      fileName={`${operatorShortName}-activity-profile`}
                    />
                  </Stack>
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <VesselPortCallsTable
          geographicalType={GEOGRAPHICAL_TYPES.ALL}
          oprId={parseInt(params.id)}
          name={operatorShortName}
          hideColumns={['operatorName', 'countryName']}
          portCalls={lastPortCallsQuery.data}
          loading={lastPortCallsQuery.isFetching}
          title={'Most Recent Vessels Port Calls'}
        />
      </Grid>
      <Grid item xs={12}>
        <UpcomingVesselPortCallsTable
          loading={upcomingPortCallsQuery.isFetching}
          assetLevel={'operator'}
          title={'Upcoming Vessels Port Calls'}
          upcomingPortCalls={upcomingPortCallsQuery.data}
          name={operatorShortName}
        />
      </Grid>
    </>
  );
}

OperatorActivitySection.propTypes = {
  operatorShortName: PropTypes.string,
  overviewDataQuery: PropTypes.object
};

export default OperatorActivitySection;
